/**
 * SCHEDULE's shared paint (migration 3071; DESIGN-CHANGELOG 2026-10-14) — the
 * pieces both trees draw identically: a cover-backed box, a stack of tiny
 * faces, the hollow coin, and the RANGE SLIDER (`design/Range Slider.dc.html`,
 * the new shared component). Everything takes its model from
 * `selectors.schedule.ts`; nothing here reads the store except the slider's
 * one write.
 *
 * Faces are INLINE MARKUP with the Avatar's recipe, not an `Avatar` per mark
 * (DESIGN-CHANGELOG 2026-09-20): a month cell or a guide block can carry a
 * dozen, and the party table a coin per row — the coin is `still` for the same
 * reason.
 */

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { useArt } from './useArt.ts';
import type { ArtCand } from '../lib/types.ts';
import { useQd } from '../store/index.ts';
import { sliderApply, sliderGrab, sliderIdxAt } from '../store/selectors.schedule.ts';
import type { SchFaceVM, SchSliderVM } from '../store/selectors.schedule.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';
const SANS = 'var(--gt-font-sans)';

/** A clock that moves the NOW line and the month's "tonight" without a store write. */
export function useSchedClock(ms = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

/** The darkening over a cover so a title reads on any art (the mirror's block scrim). */
export const SCH_SCRIM = 'linear-gradient(90deg,rgba(5,5,8,.94) 0%,rgba(5,5,8,.86) 55%,rgba(5,5,8,.5) 100%)';

/** A box painted with a game's capsule art, or its fallback gradient until there is some. */
export function SchCover({
  id,
  cands,
  fall,
  style,
  title,
  onClick,
  children,
}: {
  id: number;
  cands: readonly ArtCand[];
  fall: string;
  style?: CSSProperties;
  title?: string;
  onClick?: () => void;
  children?: ReactNode;
}): JSX.Element {
  const art = useArt(id + 'p', cands);
  return (
    <div title={title} onClick={onClick} style={{ ...style, background: art.hasCover ? art.cover : fall || 'var(--gt-tile)' }}>
      {children}
    </div>
  );
}

/** One square face — a backer, a nominator — at `px`. */
export function SchFace({ f, px, fs, bd, style }: { f: SchFaceVM; px: number | string; fs: number | string; bd?: string; style?: CSSProperties }): JSX.Element {
  const size = typeof px === 'number' ? px + 'px' : px;
  return (
    <div
      title={f.nm}
      style={{
        width: size,
        height: size,
        flex: 'none',
        boxSizing: 'border-box',
        background: f.hasAv ? f.avBg : f.bg,
        border: '1px solid ' + (bd ?? 'var(--gt-ink)'),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: PIXEL,
        fontSize: typeof fs === 'number' ? fs + 'px' : fs,
        color: f.ink,
        ...style,
      }}
    >
      {f.hasAv ? null : f.ini}
    </div>
  );
}

/** The backers in a block's corner: overlapping, yours accent-bordered. */
export function SchFaceStack({ faces, px = 14 }: { faces: readonly SchFaceVM[]; px?: number }): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', flex: 'none' }}>
      {faces.map((q) => (
        <SchFace key={q.key} f={q} px={px} fs={6} bd={q.bd} style={{ marginLeft: q.ml, position: 'relative', zIndex: q.z }} />
      ))}
    </div>
  );
}

/** The hollow coin on PUT MY QUARTER DOWN — the quarter you have not spent. */
export function CoinOff({ px = 21 }: { px?: number }): JSX.Element {
  return (
    <svg width={px} height={px} viewBox="0 0 14 14" shapeRendering="crispEdges" style={{ display: 'block', flex: 'none' }} aria-hidden="true">
      <path
        d="M5 0h4v1H5zM3 1h8v1H3zM2 2h10v1H2zM1 3h12v2H1zM0 5h14v4H0zM1 9h12v2H1zM2 11h10v1H2zM3 12h8v1H3zM5 13h4v1H5z"
        fill="var(--gt-text-5)"
      />
      <path d="M4 1h6v1H4zM3 2h8v1H3zM2 3h10v2H2zM1 5h12v4H1zM2 9h10v2H2zM3 11h8v1H3zM4 12h6v1H4z" fill="var(--gt-ink)" />
    </svg>
  );
}

/**
 * RANGE SLIDER — the SCHEDULE's time range (`design/Range Slider.dc.html`). A
 * slice of the guide: hour labels, gridlines, the game's block as the range,
 * accent grips as the handles, an accent time flag over each handle, ANY TIME
 * snapping both ends open. The ends mean open, never a bound.
 *
 * Dragging writes the store directly (`schSetRange`), so every frame of the
 * drag is the same `sl` model the page renders from — the handles cannot drift
 * from the numbers.
 */
export function RangeSlider({ sl, phone }: { sl: SchSliderVM; phone?: boolean }): JSX.Element {
  const setRange = useQd((s) => s.schSetRange);
  const drag = useRef<'a' | 'b' | null>(null);
  const live = useRef(sl);
  live.current = sl;
  const track = useRef<HTMLDivElement | null>(null);
  const anyH = phone ? 36 : 26;
  const gripW = phone ? 12 : 8;
  const gripPad = phone ? 18 : 14;

  const idxAt = (clientX: number): number => {
    const r = track.current?.getBoundingClientRect();
    return r ? sliderIdxAt(clientX, r) : 0;
  };
  const apply = (h: 'a' | 'b', i: number): void => {
    const cur = live.current;
    const next = sliderApply(h, i, cur.a, cur.b);
    if (next) setRange(next.from, next.to);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.1em', color: 'var(--gt-muted-2)' }}>WHEN</span>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', color: 'var(--gt-text)' }}>{sl.txt}</span>
        <div
          onClick={() => setRange(null, null)}
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            height: anyH,
            padding: '0 10px',
            border: '1px solid ' + sl.anyBd,
            color: sl.anyClr,
            background: sl.anyBg,
            fontFamily: MONO,
            fontSize: '8.5px',
            letterSpacing: '.06em',
            cursor: 'pointer',
            boxSizing: 'border-box',
            whiteSpace: 'nowrap',
            flex: 'none',
          }}
        >
          ANY TIME
        </div>
      </div>
      <div style={{ position: 'relative' }}>
        <div
          data-sl="1"
          onPointerDown={(e) => {
            const i = idxAt(e.clientX);
            const h = sliderGrab(i, live.current.a, live.current.b);
            drag.current = h;
            try {
              e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
              /* a synthetic event has no pointer to capture */
            }
            apply(h, i);
          }}
          onPointerMove={(e) => {
            if (drag.current) apply(drag.current, idxAt(e.clientX));
          }}
          onPointerUp={() => {
            drag.current = null;
          }}
          onPointerCancel={() => {
            drag.current = null;
          }}
          style={{ position: 'relative', width: '100%', userSelect: 'none', touchAction: 'none', cursor: 'ew-resize' }}
        >
          <div style={{ position: 'relative', height: 22 }}>
            {sl.labels.map((t) => (
              <span
                key={t.key}
                style={{ position: 'absolute', left: t.left, bottom: 4, transform: t.tx, fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.05em', color: 'var(--gt-text-5)', whiteSpace: 'nowrap', visibility: t.vis }}
              >
                {t.l}
              </span>
            ))}
            {[
              { k: 'a', left: sl.aL, tx: sl.aTx, l: sl.aLbl },
              { k: 'b', left: sl.bL, tx: sl.bTx, l: sl.bLbl },
            ].map((f) => (
              <div
                key={f.k}
                style={{ position: 'absolute', left: f.left, bottom: 0, transform: f.tx, background: 'var(--acc)', color: 'var(--gt-ink)', fontFamily: MONO, fontSize: '8.5px', fontWeight: 600, letterSpacing: '.06em', padding: '3px 6px', whiteSpace: 'nowrap', zIndex: 4 }}
              >
                {f.l}
              </div>
            ))}
          </div>
          <div ref={track} data-slt="1" style={{ position: 'relative', height: 64, borderTop: '1px solid var(--gt-border)', borderBottom: '1px solid var(--gt-border)' }}>
            {sl.ticks.map((t) => (
              <div key={t.key} style={{ position: 'absolute', top: 0, bottom: 0, left: t.left, borderLeft: '1px ' + t.st + ' var(--gt-line-0)' }} />
            ))}
            <SchCover
              id={sl.id}
              cands={sl.coverCands}
              fall={sl.hasGame ? sl.fall : 'var(--gt-tile)'}
              style={{
                position: 'absolute',
                left: sl.aL,
                width: sl.fillW,
                top: 4,
                height: 56,
                boxSizing: 'border-box',
                border: '1px solid var(--acc)',
                borderLeft: '1px ' + sl.aSt + ' var(--acc)',
                borderRight: '1px ' + sl.bSt + ' var(--acc)',
                overflow: 'hidden',
                zIndex: 2,
                boxShadow: 'var(--gt-shadow-btn)',
              }}
            >
              <div style={{ position: 'absolute', inset: 0, background: SCH_SCRIM }} />
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', padding: '7px ' + gripPad + 'px', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <span style={{ fontFamily: SANS, fontSize: 12, fontWeight: 500, color: sl.nmClr, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sl.nm}</span>
                  <span style={{ fontFamily: PIXEL, fontSize: 7, color: sl.tClr, transform: 'translateY(0.13em)' }}>{sl.tLet}</span>
                </div>
                <span style={{ fontFamily: MONO, fontSize: '7.5px', letterSpacing: '.05em', color: 'var(--acc)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sl.txt}</span>
              </div>
              {[{ k: 'l', side: { left: 0 } }, { k: 'r', side: { right: 0 } }].map((g) => (
                <div
                  key={g.k}
                  style={{ position: 'absolute', ...g.side, top: 0, bottom: 0, width: gripW, background: 'var(--acc)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}
                >
                  <div style={{ width: 2, height: 14, background: 'var(--gt-ink)' }} />
                </div>
              ))}
            </SchCover>
          </div>
        </div>
      </div>
    </div>
  );
}
